import { Ionicons } from '@expo/vector-icons';
import React, { forwardRef } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { BrandWordmark } from '@/components/ui/BrandWordmark';
import { palette } from '@/config/theme';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

export interface ScoreShareData {
  homeName: string;
  awayName: string;
  homeScore: number;
  awayScore: number;
  pickHome: number;
  pickAway: number;
  points: number;
}

/**
 * The branded card we rasterise to a PNG for the "share as image" action. It is
 * mounted OFF-SCREEN (parent positions it absolutely with a negative offset) so
 * the user never sees it - `react-native-view-shot` still captures it because
 * `collapsable={false}` keeps the native view alive. Colours come from the
 * STATIC dark `palette` (not the theme hook) on purpose: a shared image should
 * always look like the brand's signature dark Volt card, regardless of whether
 * the viewer is currently in light mode.
 *
 * Team names are rendered as text (short names) rather than remote logos: an
 * off-screen view can't guarantee a network image has decoded by capture time,
 * and a half-loaded logo would leak into the export.
 */
export const SCORE_SHARE_CARD_WIDTH = 360;

export const ScoreShareCard = forwardRef<View, { data: ScoreShareData }>(function ScoreShareCard(
  { data },
  ref,
) {
  const { t } = useTranslation();
  const { numeralOptions, isRTL } = useLocale();
  const n = (x: number) => formatNumber(x, numeralOptions);

  const exact = data.points >= 5;
  const accent = exact ? palette.win : data.points >= 2 ? palette.captain : palette.voltBright;

  // Final scoreline, mirrored in RTL so it reads home-on-left visually (the
  // app's RTL score gotcha: RN forces Text LTR, so we reverse the string).
  const finalScore = isRTL
    ? `${n(data.awayScore)} - ${n(data.homeScore)}`
    : `${n(data.homeScore)} - ${n(data.awayScore)}`;
  const pickScore = isRTL
    ? `${n(data.pickAway)} - ${n(data.pickHome)}`
    : `${n(data.pickHome)} - ${n(data.pickAway)}`;

  return (
    <View
      ref={ref}
      collapsable={false}
      style={{
        width: SCORE_SHARE_CARD_WIDTH,
        backgroundColor: palette.deep,
        borderRadius: 28,
        borderWidth: 1,
        borderColor: accent,
        paddingHorizontal: 24,
        paddingTop: 22,
        paddingBottom: 24,
        overflow: 'hidden',
      }}
    >
      {/* Header: wordmark + game label */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 18,
        }}
      >
        <BrandWordmark size={20} />
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 5,
            backgroundColor: palette.voltGlass,
            borderColor: palette.voltLine,
            borderWidth: 1,
            borderRadius: 999,
            paddingHorizontal: 10,
            paddingVertical: 4,
          }}
        >
          <Ionicons name="football-outline" size={12} color={palette.voltBright} />
          <AppText variant="micro" weight="bold" style={{ color: palette.voltBright }}>
            {t('scorePredict.title')}
          </AppText>
        </View>
      </View>

      {/* Result badge */}
      <View style={{ alignItems: 'center', marginBottom: 14 }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            backgroundColor: exact ? 'rgba(52,211,153,0.14)' : 'rgba(95,233,255,0.12)',
            borderColor: accent,
            borderWidth: 1,
            borderRadius: 999,
            paddingHorizontal: 14,
            paddingVertical: 6,
          }}
        >
          <Ionicons name={exact ? 'trophy' : 'star'} size={14} color={accent} />
          <AppText variant="caption" weight="bold" style={{ color: accent }}>
            {exact ? t('shareCard.exactHit') : t('shareCard.points', { pts: n(data.points) })}
          </AppText>
        </View>
      </View>

      {/* Teams + final score */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <AppText
          variant="heading"
          weight="bold"
          numberOfLines={1}
          style={{ flex: 1, color: palette.text, textAlign: 'center' }}
        >
          {data.homeName}
        </AppText>
        <View
          style={{
            backgroundColor: palette.surface2,
            borderRadius: 14,
            paddingHorizontal: 16,
            paddingVertical: 8,
            minWidth: 86,
            alignItems: 'center',
          }}
        >
          <AppText
            weight="bold"
            tabular
            style={{ color: palette.text, fontSize: 26, letterSpacing: 1 }}
          >
            {finalScore}
          </AppText>
        </View>
        <AppText
          variant="heading"
          weight="bold"
          numberOfLines={1}
          style={{ flex: 1, color: palette.text, textAlign: 'center' }}
        >
          {data.awayName}
        </AppText>
      </View>

      {/* Your pick */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          borderTopWidth: 1,
          borderTopColor: palette.line,
          paddingTop: 14,
        }}
      >
        <AppText variant="caption" style={{ color: palette.muted }}>
          {t('shareCard.yourPick')}
        </AppText>
        <AppText variant="caption" weight="bold" tabular style={{ color: palette.voltBright }}>
          {pickScore}
        </AppText>
      </View>
    </View>
  );
});
